import React, { useContext } from 'react';
import { Checkbox, FormControlLabel, Grid, Typography } from '@mui/material';
import ValidTextFieldTS from '../train/ValidTextFieldTS';
import { AppContext } from '../../App';
import { DocumentDispatchContext, DocumentStateContext } from '../../context/DocumentContext';
import { useCreateTrainingSet } from '../hooks/useCreateTrainingSet';
import { useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import DialogGlynt from '../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../common/Dialog/DialogActionsGlynt';

import errorResponses from '../../errorResponses';

function DocumentTableCreateTrainingSet() {
  const { setToast, dataPoolId } = React.useContext(AppContext);
  const { createTrainingSetModal, trainingSetLabel } = useContext(DocumentStateContext);
  const [disabled, setDisabled] = React.useState(true);
  const [forwardToTrainingSetPage, setForwardToTrainingSetPage] = React.useState(false);

  const dispatch = useContext<React.Dispatch<any>>(DocumentDispatchContext);

  const cache = useQueryClient();

  const navigate = useNavigate();
  const { search } = useLocation();

  const searchParams = new URLSearchParams(search);
  const selectedIds = searchParams.get('selectedIds')?.split(',') || [];

  const { mutateAsync: createTrainingSet, isPending: isLoading } = useCreateTrainingSet();

  const setLabel = (label: string) => {
    dispatch({ type: 'SET_TRAINING_SET_LABEL', label });
  };

  const closeModal = () => {
    dispatch({ type: 'TOGGLE_CREATE_TRAINING_SET_MODAL' });
  };

  const handleSubmit = async () => {
    try {
      const data = await createTrainingSet({
        label: trainingSetLabel,
        documents: selectedIds,
      });

      if (forwardToTrainingSetPage) {
        cache.invalidateQueries({ queryKey: ['trainingSets'] });
        navigate(`/data-pools/${dataPoolId}/train/${data.id}/details`);
      } else {
        closeModal();
        setToast({
          open: true,
          variant: 'success',
          message: 'Training set created successfully!',
        });
      }
    } catch (e) {
      setToast({
        open: true,
        variant: 'error',
        message: errorResponses.failedToCreateTrainingSet,
      });
    }
  };

  return (
    <DialogGlynt
      open={createTrainingSetModal}
      closeModal={closeModal}
      cypressId="dialog-create-training-set"
      loading={isLoading}
    >
      <DialogTitleGlynt title="Create New Training Set" closeModal={closeModal} loading={isLoading} />
      <DialogContentGlynt>
        <ValidTextFieldTS
          placeholder="Training Set Name"
          cypressId="input-new-training-set-name"
          setLabel={setLabel}
          disabled={false}
          setDisabled={setDisabled}
        />
        <FormControlLabel
          sx={{ mt: 1.5 }}
          control={
            <Checkbox
              color="primary"
              checked={forwardToTrainingSetPage}
              inputProps={
                {
                  'data-cy': `checkbox-forward-to-training-set-page`,
                } as React.InputHTMLAttributes<HTMLInputElement>
              }
              onChange={(e) => {
                setForwardToTrainingSetPage(e.target.checked);
              }}
            />
          }
          label="Forward to Training Set Details Page?"
        />
      </DialogContentGlynt>
      <DialogActionsGlynt
        handleSubmit={handleSubmit}
        closeModal={closeModal}
        cancelCypressId="training-set-new-dialog-cancel"
        submitCypressId="button-create-training-set-next"
        clearCypressId=""
        submitButtonText="Create"
        submitDisabled={disabled}
        submitLoading={isLoading}
      />
    </DialogGlynt>
  );
}

export default DocumentTableCreateTrainingSet;
